import React, { useLayoutEffect, useRef, useState } from "react";
import "./VerticalDividerWithOr.css";

/**
 * VerticalDividerWithOr
 * Renders a slim vertical divider with a centered label (default: "OR").
 *
 * Props:
 * - label: string (default "OR")
 * - height: CSS length | number (default 120) — total height of the divider
 * - lineColor: CSS color (default "#94A3B8") — slate-400
 * - lineWidth: CSS length | number (default 1)
 * - gap: CSS length | number (default 6) — extra breathing space around the label
 * - labelBg: CSS color (default "#FFFFFF")
 * - labelColor: CSS color (default "#334155") — slate-700
 * - fontSize: CSS length | number (default "0.85rem")
 * - fontWeight: string | number (default 600)
 * - rounded: CSS radius (default "4px")
 * - className: extra class for wrapper
 * - style: inline style for wrapper
 */
export default function VerticalDividerWithOr({
  label = "OR",
  height = 120,
  lineColor = "#94A3B8",
  lineWidth = 1,
  gap = 6,
  labelBg = "#FFFFFF",
  labelColor = "#334155",
  fontSize = "0.85rem",
  fontWeight = 600,
  rounded = "4px",
  className = "",
  style = {},
}) {
  const labelRef = useRef(null);
  const [labelH, setLabelH] = useState(0);

  useLayoutEffect(() => {
    if (!labelRef.current) return;
    const rect = labelRef.current.getBoundingClientRect();
    setLabelH(rect.height);
  }, [label, fontSize, fontWeight]);

  // Normalize numeric props to px for CSS variables
  const toCss = (v) => (typeof v === "number" ? `${v}px` : String(v));

  return (
    <div
      className={`vdwo-wrap ${className}`}
      style={{
        ...style,
        ["--vdwo-h"]: toCss(height),
        ["--vdwo-color"]: lineColor,
        ["--vdwo-w"]: toCss(lineWidth),
        ["--vdwo-gap"]: toCss(gap),
        ["--vdwo-label-h"]: `${labelH}px`,
      }}
      aria-hidden="true"
    >
      <div className="vdwo-line" />
      <span
        ref={labelRef}
        className="vdwo-label"
        style={{
          background: labelBg,
          color: labelColor,
          fontSize,
          fontWeight,
          borderRadius: rounded,
        }}
      >
        {label}
      </span>
    </div>
  );
}
